import React from 'react';
import PropTypes from 'prop-types';
import ERSTire from 'components/ERSTire';
import LabeledData from 'components/LabeledData';
import { Redirect, withRouter } from 'react-router-dom';
import { Typography, Link } from '@material-ui/core';
import ArrowBackRoundedIcon from '@material-ui/icons/ArrowBackRounded';
import { useTheme } from '@material-ui/core/styles';

const createStyles = (theme) => ({
  firstDataTitle: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    marginTop: '.75rem',
  },
  headerCard: {
    backgroundColor: `${theme.palette.primary.main}`,
    height: '4rem',
  },
  headerSubtitle: {
    marginLeft: '1rem',
    fontSize: '.75rem',
  },
  dataTitle: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    marginTop: '1.25rem',
  },
});

function EventViewer({ location, history }) {
  const theme = useTheme();
  const styles = React.useMemo(() => createStyles(theme), [theme]);
  if (!location.state) {
    return <Redirect to="/" />;
  }

  const { event } = location.state;

  return (
    <div className="container-fluid">
      <div className="row align-items-center" style={styles.headerCard}>
        <div className="pull-left align-middle" style={{ marginLeft: '1rem' }}>
          <Link href="/">
            <ArrowBackRoundedIcon
              style={{
                color: theme.palette.getContrastText(
                  styles.headerCard.backgroundColor,
                ),
              }}
              id="goHomeButton"
            />
          </Link>
        </div>
        <div
          className="pull-left align-middle text-white"
          style={styles.headerSubtitle}
        >
          Michelin&reg; OnCall 2.0
        </div>
        <div
          className="col text-center d-none d-md-block d-lg-block text-white"
          style={{ fontSize: '1.25rem' }}
        >
          ERS Event Viewer Summary
        </div>
      </div>
      <div className="row" style={{ paddingTop: '.25rem' }}>
        <div className="col" style={{ fontSize: '0.75rem' }}>
          To view a dashboard of all of your calls, click the following link and
          authenticate as a fleet or dealer: &nbsp;
          <Link
            href="https://launch-pad.oncall.misp-solutions.com"
            underline="none"
          >
            <Typography color="primary" variant="caption" id="ersLaunchPadLink">
              <u>ERS Launch Pad</u>
            </Typography>
          </Link>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Typography style={styles.firstDataTitle} variant="subtitle1">
            ERS Breakdown Details
          </Typography>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 col-xl-3">
          <LabeledData label="Case Number" data={event.eventId} row={false} />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 col-xl-3">
          <LabeledData label="Event Date" data={event.eventDate} />
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-3">
          <LabeledData
            label="Downtime Start"
            data={event.downtimeStart}
            row={false}
          />
        </div>
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-3">
          <LabeledData
            label="Downtime Stop"
            data={event.downtimeStop}
            row={false}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-4 col-lg-6 col-xl-3">
          <LabeledData
            label="Total Downtime"
            data={event.totalDowntime}
            row={false}
          />
        </div>
      </div>
      <div className="row">
        <div
          className="col font-italic"
          style={{ fontSize: '0.65rem', marginTop: '.25rem' }}
        >
          *Note: All times are presented in Eastern Time.
        </div>
      </div>

      <div className="row">
        <div className="col">
          <Typography style={styles.dataTitle} variant="subtitle1">
            Customer Details
          </Typography>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-4">
          <LabeledData label="Fleet Name" data={event.fleetName} />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 col-xl-3">
          <LabeledData
            label="Fleet Ship To #"
            data={event.fleetShipTo}
            row={false}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 col-xl-3">
          <LabeledData label="Unit Type" data={event.vehicleType} row={false} />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6 col-xl-2">
          <LabeledData label="Unit Number" data={event.vehicleId} />
        </div>
        <div className="col">
          <LabeledData label="Breakdown Location" data={event.address} />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <Typography style={styles.dataTitle} variant="subtitle1">
            Billing Details
          </Typography>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
          <LabeledData label="PO Number" data={event.poNumber} row={false} />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
          <LabeledData
            label="Reference Number"
            data={event.referenceNumber}
            row={false}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
          <LabeledData
            label="Invoice Number"
            data={event.invoiceNumber}
            row={false}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
          <LabeledData
            label="DR/Ticket Number"
            data={event.ticketNumber}
            row={false}
          />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
          <LabeledData label="Amount" data={event.billingAmount} row={false} />
        </div>
        <div className="col-xs-12 col-sm-6 col-md-6 col-lg-4 col-xl-3">
          <LabeledData
            label="Billing Comments"
            data={event.billingComments}
            row={false}
          />
        </div>
      </div>
      <div className="row">
        <div
          className="col font-italic"
          style={{ fontSize: '0.65rem', marginTop: '.25rem' }}
        >
          *DEALER NOTE : If PO not listed you are requested to call the Fleet
          Contact Number to obtain.
        </div>
      </div>

      <div className="row">
        <div className="col">
          <Typography style={styles.dataTitle} variant="subtitle1">
            Servicing Dealer Information
          </Typography>
        </div>
      </div>
      <div className="row">
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-4">
          <LabeledData label="Name" data={event.dealerStoreName} />
        </div>
        <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6 col-xl-3">
          <LabeledData label="Ship To #" data={event.dealerStoreShipTo} />
        </div>
        <div className="col-xs-12 col-xl-5">
          <LabeledData
            label="Store Location"
            data={event.dealerAddress}
            row={false}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <Typography style={styles.dataTitle} variant="subtitle1">
            ERS Resolution
          </Typography>
        </div>
      </div>
      <div className="row" style={{ paddingBottom: '1rem' }}>
        <div className="col">
          {event.products &&
            event.products.map((tire, index) => (
              <ERSTire
                tire={{
                  order: {},
                  invoice: {},
                  estimate: {},
                  ...tire,
                }}
                key={tire.key}
                index={index}
                showHeading
              />
            ))}
          {!event.products && `No tire data`}
        </div>
      </div>
    </div>
  );
}

EventViewer.propTypes = {
  location: PropTypes.shape({
    event: PropTypes.object,
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }).isRequired,
};

export default withRouter(EventViewer);
